
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";

// Define production URL
const PRODUCTION_URL = "https://everleytraining.com";

export const useSession = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const [isMagicLinkSignIn, setIsMagicLinkSignIn] = useState(false);

  const { data: session, isLoading: isSessionLoading } = useQuery({
    queryKey: ["session"],
    queryFn: async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.error("Session error:", error);
          // If we get a refresh token error, clear the session
          if (error.message.includes('refresh_token')) {
            await supabase.auth.signOut();
            queryClient.setQueryData(["session"], null);
            
            // Use the production URL for redirects if in production
            const isProduction = window.location.origin.includes('everleytraining.com');
            const redirectUrl = isProduction ? PRODUCTION_URL : '/';
            
            navigate(redirectUrl);
            toast({
              title: "Session expired",
              description: "Please sign in again to continue.",
              variant: "destructive",
            });
          }
          return null;
        }
        return session;
      } catch (error) {
        console.error("Session error:", error);
        return null;
      }
    },
    retry: false,
    staleTime: 1000 * 60 * 5, // Consider the session data fresh for 5 minutes
    gcTime: 1000 * 60 * 30, // Keep the data cached for 30 minutes
  });

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("Auth state change:", event);
      
      if (event === 'SIGNED_OUT') {
        queryClient.setQueryData(["session"], null);
        
        // Use the production URL for redirects if in production
        const isProduction = window.location.origin.includes('everleytraining.com');
        const redirectUrl = isProduction ? PRODUCTION_URL : '/';
        
        navigate(redirectUrl);
      } else if (event === 'SIGNED_IN' && session) {
        queryClient.setQueryData(["session"], session);
        
        // Check if the user signed in with magic link (provider is 'email')
        if (session.user?.app_metadata?.provider === 'email' && 
            !session.user?.user_metadata?.has_set_password) {
          setIsMagicLinkSignIn(true);
        }
      } else if (event === 'TOKEN_REFRESHED' && session) {
        queryClient.setQueryData(["session"], session);
      } else if (event === 'PASSWORD_RECOVERY' && session) {
        setIsPasswordRecovery(true);
        
        // When a password is updated as part of recovery, mark that the user has set a password
        if (session.user) {
          await supabase.auth.updateUser({
            data: { has_set_password: true }
          });
        }
      } else if (event === 'USER_UPDATED' && session) {
        queryClient.setQueryData(["session"], session);
        
        // If this was a password reset, navigate to training
        if (isPasswordRecovery) {
          setIsPasswordRecovery(false);
          navigate('/training');
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [queryClient, navigate, isPasswordRecovery]);

  // Update the ResetPasswordDialog component to mark has_set_password as true
  // when a password is set
  useEffect(() => {
    if (session && isMagicLinkSignIn) {
      // This effect will run once after a magic link sign-in is detected
      setIsMagicLinkSignIn(false);
    }
  }, [session, isMagicLinkSignIn]);

  return { 
    session, 
    isSessionLoading, 
    isPasswordRecovery,
    isMagicLinkSignIn
  };
};
